import { AI_PROVIDERS, DEFAULT_MODELS, getAiSettings, providerMeta } from './aiSettings';
import { hasAiKey, vaultKeyState } from './keyStore';
import { getApibooStatus } from './apibooStatus';
import { migrateDefaultToApibooOnce } from './apibooDefaultMigration';

/**
 * Which provider will the next turn actually use? (22.08.2026)
 *
 * One place answers that for the engine (runAiTurn, checkAiReadiness) and the
 * panel (setup state, fallback strip, privacy copy), so they cannot disagree.
 *
 * Resolves
 *   { state, provider, model, via, fallbackFrom, apiboo }
 *
 *   state        'ready' | 'no_key' | 'vault_locked' | 'no_vault'
 *                — the panel still renders AiKeySetupState for anything but
 *                'ready'; a fallback onto Apiboo IS 'ready'.
 *   via          'byo' | 'apiboo' | null (null only when not ready)
 *   provider     the provider the turn goes to ('apiboo' when via is 'apiboo')
 *   model        the model id sent (the 'apiboo' sentinel for the server path —
 *                the server chooses; `apiboo.model` carries the real id for display)
 *   fallbackFrom null | 'no_key' | 'vault_locked' | 'no_vault' | 'apiboo_unavailable'
 *                — why the selected provider was not used
 *   apiboo       the status object when it was consulted, else null
 *   selectedProvider  what Settings says, regardless of what was resolved
 *
 * Rules (the owner's: BYO is primary whenever a usable key exists; Apiboo is
 * the always-working default/fallback):
 *   - selected provider is keyless (Apiboo) and available   → via 'apiboo'
 *   - selected provider is keyless but unavailable          → the first BYO
 *     provider with a saved key, else 'no_key'
 *   - selected BYO provider with a readable key             → via 'byo'
 *   - selected BYO provider without one, Apiboo available   → via 'apiboo' + fallbackFrom
 *   - selected BYO provider without one, Apiboo unavailable → the old dead end
 *     (state = the reason), which is the only case the setup state still exists for
 *
 * The Apiboo path never touches the vault: no vault_get_item, no key read.
 */

/** 'unlocked' | 'locked' | 'unavailable' — never throws. */
async function keyReadiness(provider) {
  const vaultState = await vaultKeyState();
  if (vaultState === 'unavailable') return 'no_vault';
  if (vaultState === 'locked') return 'vault_locked';
  try {
    return (await hasAiKey(provider)) ? null : 'no_key';
  } catch {
    return 'vault_locked';
  }
}

/** The first BYO provider with a key in an unlocked vault, or null. */
async function firstKeyedByoProvider() {
  if ((await vaultKeyState()) !== 'unlocked') return null;
  for (const p of AI_PROVIDERS) {
    if (p.keyless) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      if (await hasAiKey(p.id)) return p.id;
    } catch {
      return null;
    }
  }
  return null;
}

export async function resolveAiTarget() {
  // One-time: an old install still on the pre-Apiboo default with no key
  // moves to the assistant (services/ai/apibooDefaultMigration.js). Runs
  // BEFORE the preference is read so this very resolution honours it.
  await migrateDefaultToApibooOnce();
  const { provider, model } = getAiSettings();
  const selectedProvider = provider;

  if (providerMeta(provider).keyless) {
    const apiboo = await getApibooStatus();
    if (apiboo.available) {
      return { state: 'ready', provider: 'apiboo', model, via: 'apiboo', fallbackFrom: null, apiboo, selectedProvider };
    }
    const keyed = await firstKeyedByoProvider();
    if (keyed) {
      return {
        state: 'ready',
        provider: keyed,
        model: DEFAULT_MODELS[keyed],
        via: 'byo',
        fallbackFrom: 'apiboo_unavailable',
        apiboo,
        selectedProvider,
      };
    }
    return { state: 'no_key', provider, model, via: null, fallbackFrom: null, apiboo, selectedProvider };
  }

  const reason = await keyReadiness(provider);
  if (!reason) {
    return { state: 'ready', provider, model, via: 'byo', fallbackFrom: null, apiboo: null, selectedProvider };
  }

  const apiboo = await getApibooStatus();
  if (apiboo.available) {
    return { state: 'ready', provider: 'apiboo', model: 'apiboo', via: 'apiboo', fallbackFrom: reason, apiboo, selectedProvider };
  }
  return { state: reason, provider, model, via: null, fallbackFrom: null, apiboo, selectedProvider };
}
